import * as React from 'react'
import type { Dispatch, SetStateAction } from 'react'

import {
  Box,
  Button,
  Variants as ButtonVariants,
  Hint,
  SelectOption,
  useClassNames,
} from '@factorialco/deprecated-design-system'
import { find, omit } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import CustomValuesModal from 'modules/payroll_bundle/components/common/components/CustomValuesModal'
import type {
  ResultMatches,
  SupplementType,
  SupplementsMatches,
  VisibleButtons,
  WorksheetData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'

import * as styles from '../styles/index.vanilla.css'
import WorksheetButton from './WorksheetButton'

interface Props {
  activeButton: string
  addData?: () => void
  processData?: () => void
  matchComplete?: boolean
  visibleButtons: VisibleButtons[]
  supplementTypes?: SupplementType[]
  buildSupplementTypeButtonOptions?: (
    button: SupplementType
  ) => Array<{ title: string; onClick: () => void }>
  worksheetMatches: ResultMatches | SupplementsMatches
  setActiveButton: (buttonName: string) => void
  worksheetData: WorksheetData
  setVisibleButtons: Dispatch<SetStateAction<VisibleButtons[]>>
  customValues?: SelectOption[]
  setWorksheetMatches: (worksheetMatches: SupplementsMatches) => void
  sequentialSelection?: boolean
  onResetAllButtons?: () => void
}

const WorksheetButtons = ({
  addData,
  processData,
  customValues,
  activeButton,
  worksheetData,
  visibleButtons,
  supplementTypes,
  buildSupplementTypeButtonOptions,
  matchComplete,
  setActiveButton,
  worksheetMatches,
  setVisibleButtons,
  setWorksheetMatches,
  sequentialSelection = false,
  onResetAllButtons,
}: Props) => {
  const cx = useClassNames(styles)
  const [customValueModal, setCustomValueModal] = React.useState<boolean>(false)

  const resetButton = (buttonName: string) => () => {
    setWorksheetMatches(omit(worksheetMatches, buttonName))
  }

  const resetAndActive = (buttonName: string) => () => {
    setWorksheetMatches(omit(worksheetMatches, buttonName))
    setActiveButton(buttonName)
  }

  const stringSupplementTypeLabel = (supplementType: SupplementType) => {
    let result = supplementType.label
    if (supplementType.mappingsCount > 1) {
      result += `+ (${supplementType.mappingsCount - 1})`
    }
    return result
  }

  const renderSplitButton = (supplementType: SupplementType) => {
    // TODO: add the Text element back once the bug with the button display is solved
    // const content = <SupplementTypeLabel supplementType={supplementType} />
    const content = stringSupplementTypeLabel(supplementType)

    let stateType: ButtonVariants | undefined
    if (supplementType.id === activeButton) {
      stateType = 'primary'
    } else if (worksheetMatches[supplementType.id]) {
      stateType = 'primary'
    } else {
      stateType = 'secondary'
    }

    if (buildSupplementTypeButtonOptions) {
      return (
        <Button.Split
          label={content}
          type={stateType}
          onClick={() => setActiveButton(supplementType.id)}
        >
          {buildSupplementTypeButtonOptions(supplementType).map((x, i) => (
            <Button.Split.Option key={i} onClick={x.onClick}>
              {x.title}
            </Button.Split.Option>
          ))}
        </Button.Split>
      )
    }
    return <Button.Decorative type={stateType}>{content}</Button.Decorative>
  }

  const addCustomValue = (customValueKey: string) => {
    const customValue = find(customValues, (cv: SelectOption) => cv.value === customValueKey)

    if (customValue) {
      const newButtons = {
        [customValueKey]: customValue.label,
      }
      setVisibleButtons([...visibleButtons, newButtons])
      setCustomValueModal(false)
    }
  }

  if (sequentialSelection) {
    return (
      <>
        <Box
          border={{
            all: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }}
          borderRadius={{
            all: 'abs008',
          }}
          style={{ width: '100%' }}
          paddingY={'s8'}
          paddingX={'s16'}
        >
          <Box
            flexDirection="row"
            gap="s16"
            flexWrap="wrap"
            style={{ width: '100%' }}
            justifyContent="flexStart"
            alignItems="center"
          >
            {visibleButtons.map((_item, index) => (
              <Box key={index}>
                <WorksheetButton
                  index={index}
                  visibleButtons={visibleButtons}
                  worksheetMatches={worksheetMatches}
                  activeButton={activeButton}
                  worksheetData={worksheetData}
                  sequentialSelection={sequentialSelection}
                  onResetButton={resetAndActive}
                  onSetActiveButton={setActiveButton}
                />
              </Box>
            ))}
          </Box>
        </Box>
        {onResetAllButtons && (
          <Box
            flexDirection="row"
            gap="s2"
            paddingY="s8"
            alignItems="center"
            onClick={() => onResetAllButtons()}
            style={{ marginLeft: 'auto' }}
          >
            <Button iconName="cross" type="secondary">
              {i18n.t('common.clear_selection')}
            </Button>
          </Box>
        )}
      </>
    )
  }

  return (
    <Box
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{
        topRight: 'abs008',
        topLeft: 'abs008',
      }}
      background="grey200"
      justifyContent="spaceBetween"
      flexDirection="row"
      padding="s24"
    >
      {!supplementTypes && !customValues ? (
        <Hint.Popover
          step={2}
          title={i18n.t('payroll_bundle.results_payments.result_upload.hint_one.step_two.title')}
          description={i18n.t(
            'payroll_bundle.results_payments.result_upload.hint_one.step_two.description'
          )}
          actionLabel={i18n.t(
            'payroll_bundle.results_payments.result_upload.hint_one.step_two.action'
          )}
          position="top left"
        >
          <Box flexDirection="row" gap="s16" flexWrap="wrap">
            {visibleButtons.map((_item, index) => (
              <Box
                key={index}
                paddingBottom="s8"
                justifyContent="center"
                className={cx('worksheetBtnWrapper')}
              >
                <WorksheetButton
                  index={index}
                  visibleButtons={visibleButtons}
                  worksheetMatches={worksheetMatches}
                  activeButton={activeButton}
                  worksheetData={worksheetData}
                  sequentialSelection={sequentialSelection}
                  onResetButton={resetButton}
                  onSetActiveButton={setActiveButton}
                />
              </Box>
            ))}
          </Box>
        </Hint.Popover>
      ) : (
        <Box flexDirection="row" gap="s16" flexWrap="wrap">
          {visibleButtons.map((_item, index) => (
            <Box
              key={index}
              paddingBottom="s8"
              justifyContent="center"
              className={cx('worksheetBtnWrapper')}
            >
              <WorksheetButton
                index={index}
                visibleButtons={visibleButtons}
                worksheetMatches={worksheetMatches}
                activeButton={activeButton}
                worksheetData={worksheetData}
                sequentialSelection={sequentialSelection}
                onResetButton={resetButton}
                onSetActiveButton={setActiveButton}
              />
            </Box>
          ))}
          {supplementTypes?.map((supplementType, index) => (
            <Box
              key={index}
              paddingBottom="s8"
              justifyContent="center"
              className={cx('worksheetBtnWrapper')}
            >
              {renderSplitButton(supplementType)}
            </Box>
          ))}
          {customValues && (
            <Button onClick={() => setCustomValueModal(true)} type="secondary" iconName="add" />
          )}
        </Box>
      )}
      {addData && (
        <Box>
          <Button onClick={addData} iconName="add">
            {i18n.t('payroll_bundle.import_supplements.upload.add_data')}
          </Button>
        </Box>
      )}
      {processData && (
        <Box>
          <Button onClick={processData} disabled={!matchComplete}>
            {i18n.t('payroll_bundle.sepa_generator.preview.process_data')}
          </Button>
        </Box>
      )}
      {customValueModal && customValues && (
        <CustomValuesModal
          onSubmitCustomValues={addCustomValue}
          onCloseModal={() => setCustomValueModal(false)}
          customValues={customValues}
        />
      )}
    </Box>
  )
}

export default WorksheetButtons
